import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Lightbulb, Sparkles, FileText, Bot, CalendarDays, Link2 } from 'lucide-react';
import { CompetitorsTab } from '@/pages/seo/CompetitorsTab';
import { OpportunitiesTab } from '@/pages/seo/OpportunitiesTab';
import { GeneratorTab } from '@/pages/seo/GeneratorTab';
import { ArticlesTab } from '@/pages/seo/ArticlesTab';
import { CalendarTab } from '@/pages/seo/CalendarTab';
import { BacklinksTab } from '@/pages/seo/BacklinksTab';

const VALID_TABS = ['competitors', 'opportunities', 'backlinks', 'generator', 'articles', 'calendar'];

export function SeoAgentPage() {
  const { language: lang } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'competitors';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedOpp, setSelectedOpp] = useState(null);

  const t = (fr, en) => lang === 'fr' ? fr : en;

  // Keep the URL in sync when the user clicks a tab from the page itself
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to sidebar deep-links that change the query string
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateFromOpp = (opportunity) => {
    setSelectedOpp(opportunity);
    setActiveTab('generator');
  };

  return (
    <div className="space-y-6" data-testid="seo-agent-page">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Bot className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">
            {t('Marketing IA', 'AI Marketing')}
          </h1>
          <p className="text-muted-foreground">
            {t('Analyse concurrentielle, opportunites et generation de contenu SEO', 'Competitive analysis, opportunities and SEO content generation')}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="competitors" className="flex items-center gap-2" data-testid="tab-competitors">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">{t('Concurrents', 'Competitors')}</span>
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="flex items-center gap-2" data-testid="tab-opportunities">
            <Lightbulb className="h-4 w-4" />
            <span className="hidden sm:inline">{t('Opportunites', 'Opportunities')}</span>
          </TabsTrigger>
          <TabsTrigger value="backlinks" className="flex items-center gap-2" data-testid="tab-backlinks">
            <Link2 className="h-4 w-4" />
            <span className="hidden sm:inline">Backlinks</span>
          </TabsTrigger>
          <TabsTrigger value="generator" className="flex items-center gap-2" data-testid="tab-generator">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">{t('Generateur', 'Generator')}</span>
          </TabsTrigger>
          <TabsTrigger value="articles" className="flex items-center gap-2" data-testid="tab-articles">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">{t('Articles', 'Articles')}</span>
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-2" data-testid="tab-calendar">
            <CalendarDays className="h-4 w-4" />
            <span className="hidden sm:inline">{t('Calendrier', 'Calendar')}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="competitors"><CompetitorsTab /></TabsContent>
        <TabsContent value="opportunities"><OpportunitiesTab onGenerate={handleGenerateFromOpp} /></TabsContent>
        <TabsContent value="backlinks"><BacklinksTab /></TabsContent>
        <TabsContent value="generator"><GeneratorTab selectedOpportunity={selectedOpp} onArticleGenerated={() => {}} /></TabsContent>
        <TabsContent value="articles"><ArticlesTab /></TabsContent>
        <TabsContent value="calendar"><CalendarTab /></TabsContent>
      </Tabs>
    </div>
  );
}
